import { InputRichText } from '@dixa/lib-shared-habla';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { SanitizationProfileType, sanitizeHtml } from 'utils/sanitizeHtml';

import { getFragmentString } from './getFragmentString';
import { hasTextContent } from './hasTextContent';

type ReplaceFn = (text: string) => void;
type InsertBelowFn = (text: string) => void;
type InsertFn = (text: string) => void;

type UseEditorContextResult = {
  /** The function for replacing the (selected) text in the editor. */
  readonly replace: ReplaceFn;
  /** The function for inserting the text below current selection. */
  readonly insertBelow: InsertBelowFn;
  /** The function for inserting the text in the editor at the cursor/selection. */
  readonly insert: InsertFn;
  /** The function for getting the selected text in the editor. */
  readonly getSelectedText: () => string;
  /** Whether part of the editor's content is selected. */
  readonly hasSelection: () => boolean;
  /** Whether the current editor selection is empty. */
  readonly isEmpty: boolean;
  /** Function to undo the last text insertion/replacement */
  readonly undo: () => void;
  /** Whether the editor has actions to undo */
  readonly canUndo: () => boolean;
};

// Since no new tags will be created, using email works for all channels as it supports the widest range of tags.
export const sanitizationProfile: SanitizationProfileType = 'email';

export function useEditorContext(): UseEditorContextResult {
  const editorContext = useContext(InputRichText.Context);

  /**
   * Gets selected text. If nothing is selected, returns the full content of the
   * text editor.
   */
  const getSelectedText = useCallback((): string => {
    if (!editorContext.ref || !editorContext.selection) {
      return '';
    }
    const range = editorContext.selection.getRange();

    if (range && !range.collapsed) {
      return getFragmentString(range.cloneContents());
    }

    return editorContext.ref.current?.innerHTML ?? '';
  }, [editorContext.ref, editorContext.selection]);

  const hasSelection = useCallback((): boolean => {
    const range = editorContext.selection?.getRange();

    return !!range && !range.collapsed;
  }, [editorContext.selection]);

  const [isEmpty, setIsEmpty] = useState<boolean>(
    () => !getSelectedText().trim(),
  );

  useEffect(() => {
    editorContext.selection.onUpdate(() => {
      const html = getSelectedText();
      setIsEmpty(!hasTextContent(html));
    });
  }, [editorContext.ref, editorContext.selection, getSelectedText]);

  /**
   * Recreates editor's selection. If nothing was previously selected, it
   * selects all contents of the editor.
   *
   * @param collapseToEnd Whether to collapse to the end of the selection once
   *   it's created. Useful when inserting content in the end of the selection,
   *   instead of replacing it.
   */
  const createEditorSelection = useCallback(
    (collapseToEnd?: boolean) => {
      const selection = editorContext.selection.recreate();
      if (selection?.rangeCount && selection?.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        if (range?.collapsed && editorContext.ref.current) {
          range.selectNodeContents(editorContext.ref.current);
        }
      }

      if (collapseToEnd) {
        selection?.collapseToEnd();
      }

      return selection;
    },
    [editorContext.ref, editorContext.selection],
  );

  /**
   * Inserts the given text under the selection. If nothing is selected, a new
   * line will be added at the end of the editor's content.
   */
  const insertBelow = useCallback<InsertBelowFn>(
    (text) => {
      if (!editorContext.selection || !editorContext.window?.document) {
        return;
      }
      editorContext.history.takeSnapshot();
      const range = editorContext.selection.getRange();

      if (range && text) {
        const div = editorContext.window.document.createElement('div');
        div.appendChild(
          // eslint-disable-next-line no-unsanitized/method
          range.createContextualFragment(
            sanitizeHtml(text, sanitizationProfile),
          ),
        );
        editorContext.content.insert(div, {
          getSelection: () => createEditorSelection(true),
        });
      }
    },
    [
      createEditorSelection,
      editorContext.content,
      editorContext.selection,
      editorContext.history,
      editorContext.window?.document,
    ],
  );

  /**
   * Replace the editor selection with the given text. If nothing is selected,
   * all content of the editor will be replaced.
   */
  const replace = useCallback<ReplaceFn>(
    (text) => {
      if (!editorContext.content || !editorContext.selection) {
        return;
      }
      editorContext.history.takeSnapshot();
      const range = editorContext.selection.getRange();

      if (range && text) {
        editorContext.content.insert(
          // eslint-disable-next-line no-unsanitized/method
          range.createContextualFragment(
            sanitizeHtml(text, sanitizationProfile),
          ),
          {
            getSelection: () => createEditorSelection(false),
          },
        );
      }
    },
    [
      createEditorSelection,
      editorContext.content,
      editorContext.selection,
      editorContext.history,
    ],
  );

  /**
   * Inserts the given text in the editor at the cursor/selection. If nothing is
   * selected, the text will be inserted at the cursor position.
   */
  const insert = useCallback<InsertFn>(
    (text) => {
      editorContext.selection.recreate();
      editorContext.history.takeSnapshot();
      const range = editorContext.selection.getRange();

      if (range && text) {
        const div = document.createElement('div');
        div.appendChild(
          // eslint-disable-next-line no-unsanitized/method
          range.createContextualFragment(
            sanitizeHtml(text, sanitizationProfile),
          ),
        );
        editorContext.content.insert(div);
      }
    },
    [editorContext.content, editorContext.selection, editorContext.history],
  );

  return useMemo(
    () => ({
      isEmpty,
      replace,
      insertBelow,
      insert,
      getSelectedText,
      hasSelection,
      undo: editorContext.history.undo,
      canUndo: editorContext.history.canUndo,
    }),
    [
      editorContext.history.canUndo,
      editorContext.history.undo,
      getSelectedText,
      hasSelection,
      insert,
      insertBelow,
      isEmpty,
      replace,
    ],
  );
}
